import { gql } from '@apollo/client';
import {
  type CreateMediaRequest,
  type CreateMediaRequestResponse,
  type DateRange,
  type EventFilters,
  type EventsSeveritySummary,
  EventTrigger,
  type Media,
  MediaType,
  type MediaView,
  type PageableContent,
  type PageableContentAPI,
  type RequestFilters,
  type UUID,
} from '@cobliteam/common-types';
import { camelCase } from 'change-case';
import graphQLRequester from '../../request/GraphQLRequester';
import type { CreateMediaRequestResponseAPI, MediaAPI, MediaViewAPI } from './definitions';
import { mediaNewFilter, mediaRequestStatusV2Filter, toCreateMediaRequestAPI, toCreateMediaRequestResponse, toMediaPage, toMediaView } from './mediaTransformer';

export class GraphQLCameraMediaRequester {
  private queryGetMedia = gql`
    query getMedia(
      $driverIds: [ID]
      $vehicleIds: [ID]
      $eventTriggers: [EventTrigger]
      $severities: [Severity]
      $requestStatus: [MediaRequestStatus]
      $fromTimestamp: TimestampInMillis
      $mediaTypes: [MediaType]
      $page: Int
      $size: Int
      $sort: [String]
      $sortList: [String]
      $toTimestamp: TimestampInMillis
      $createdBy: [ID]
      $isNew: Boolean
      $fromCreatedAtTimestamp: TimestampInMillis
      $toCreatedAtTimestamp: TimestampInMillis
      $isFavorite: Boolean
      $safetyOccurrenceIds: [ID]
      $mediaStatus: [String]
      $isRequestedByTrigger: Boolean
      $triggerIds: [ID]
      $descriptionTextQuery: String
      $suggestedOccurrences: [String]
    ) {
      media(
        driverIds: $driverIds
        vehicleIds: $vehicleIds
        eventTriggers: $eventTriggers
        severities: $severities
        fromTimestamp: $fromTimestamp
        requestStatus: $requestStatus
        mediaTypes: $mediaTypes
        page: $page
        size: $size
        sort: $sort
        sortList: $sortList
        toTimestamp: $toTimestamp
        createdBy: $createdBy
        isNew: $isNew
        fromCreatedAtTimestamp: $fromCreatedAtTimestamp
        toCreatedAtTimestamp: $toCreatedAtTimestamp
        isFavorite: $isFavorite
        safetyOccurrenceIds: $safetyOccurrenceIds
        mediaStatus: $mediaStatus
        isRequestedByTrigger: $isRequestedByTrigger
        triggerIds: $triggerIds
        descriptionTextQuery: $descriptionTextQuery
        suggestedOccurrences: $suggestedOccurrences
      ) {
        content {
          id
          device_id
          driver_id
          driver {
            id
            name
          }
          unreviewed_driver {
            id
            name
          }
          media_type
          media_status
          safety_review {
            id
            fleet_id
            status
            driver_id
            driver {
              id
              name
            }
            created_by
            updated_by
            updated_by_user {
              full_name
            }
            created_at
            updated_at
            occurrences {
              id
              name
            }
            dismiss_reason
          }
          media_details {
            ... on EventVideo {
              event_trigger
              latitude
              longitude
              event_media_url: media_url
              speed
              speed_sign
              event_thumbnail_url: thumbnail_url
            }
            ... on FootageVideo {
              created_at
              duration
              end_latitude
              end_longitude
              footage_media_url: media_url
              request_id
              start_latitude
              start_longitude
              status
              request_status
              footage_thumbnail_url: thumbnail_url
              updated_at
              created_by {
                id
                full_name
                fleet_id
                email_address
              }
              created_by_trigger {
                id
                name
              }
            }
          }
          vehicle {
            id
            driver {
              id
              name
              phoneNumbers
            }
            model {
              brand
              name
            }
            licensePlate
            alias
          }
          vehicle_group {
            id
            name
          }
          media_view_count
          timestamp
          severity
          is_favorite
          ai_suggested_driver {
            id
            name
          }
          ai_video_tags {
            seatbelt_detection {
              with_seatbelt
            }
          }
        }
        empty
        first
        last
        number
        number_of_elements
        size
        total_elements
        total_pages
      }
    }
  `;

  private queryGetDeviceMediaCount = gql`
    query getMedia(
      $fromTimestamp: TimestampInMillis
      $toTimestamp: TimestampInMillis
      $mediaTypes: [MediaType]
      $vehicleIds: [ID]
      $eventTriggers: [EventTrigger]
    ) {
      media(
        vehicleIds: $vehicleIds
        fromTimestamp: $fromTimestamp
        toTimestamp: $toTimestamp
        mediaTypes: $mediaTypes
        eventTriggers: $eventTriggers
      ) {
        total_elements
      }
    }
  `;

  private mutateCreateMediaRequest = gql`
    mutation createMedia($createMediaRequest: CreateMediaRequest!) {
      createMedia(input: $createMediaRequest) {
        vehicle_id
        start_time
        end_time
        media_id
      }
    }
  `;

  private queryGetMediaView = gql`
    query getMediaView($mediaId: ID, $shouldGenerateDescription: Boolean) {
      mediaView(mediaId: $mediaId, shouldGenerateDescription: $shouldGenerateDescription) {
        media_url
        expiration_date
        ai_description
        suggested_occurrences
        ai_video_tags {
          seatbelt_detection {
            with_seatbelt
          }
        }
        videos {
          url
          channel
        }
        media_info {
          id
          device_id
          driver_id
          driver {
            id
            name
          }
          unreviewed_driver {
            id
            name
          }
          media_type
          media_status
          is_favorite
          comments {
            id
            media_id
            created_by
            created_by_user {
              full_name
            }
            content
            created_at
            updated_at
          }
          safety_review {
            id
            fleet_id
            status
            driver_id
            severity
            original_media_severity
            dismiss_reason
            driver {
              id
              name
            }
            created_by
            updated_by
            updated_by_user {
              full_name
            }
            created_at
            updated_at
            occurrences {
              id
              name
            }
          }
          media_details {
            ... on EventVideo {
              event_trigger
              latitude
              longitude
              event_media_url: media_url
              speed
              speed_sign
              event_thumbnail_url: thumbnail_url
            }
            ... on FootageVideo {
              created_at
              duration
              end_latitude
              end_longitude
              footage_media_url: media_url
              request_id
              start_latitude
              start_longitude
              status
              footage_thumbnail_url: thumbnail_url
              updated_at
              created_by {
                id
                full_name
                fleet_id
                email_address
              }
              created_by_trigger {
                id
                name
              }
            }
          }
          media_view_count
          timestamp
          severity
          severity_detail {
            id
            severity_level
            severity_score
            risk_factors {
              id
              type
              value
              custom_details
            }
          }
          vehicle {
            id
            licensePlate
          }
          vehicle_group {
            id
            name
          }
          ai_suggested_driver {
            id
            name
          }
        }
      }
    }
  `;

  private queryEventsSeveritiesSummary = gql`
    query eventsSeveritiesSummary(
      $driverIds: [ID]
      $vehicleIds: [ID]
      $eventTriggers: [EventTrigger]
      $fromTimestamp: TimestampInMillis
      $toTimestamp: TimestampInMillis
      $isFavorite: Boolean
      $safetyOccurrenceIds: [ID]
      $mediaStatus: [String]
      $descriptionTextQuery: String
      $suggestedOccurrences: [String]
    ) {
      eventsSeveritiesSummary(
        driverIds: $driverIds
        vehicleIds: $vehicleIds
        eventTriggers: $eventTriggers
        fromTimestamp: $fromTimestamp
        toTimestamp: $toTimestamp
        isFavorite: $isFavorite
        safetyOccurrenceIds: $safetyOccurrenceIds
        mediaStatus: $mediaStatus
        descriptionTextQuery: $descriptionTextQuery
        suggestedOccurrences: $suggestedOccurrences
      ) {
        low
        medium
        high
        higher
      }
    }
  `;

  private getArrayKeys = (obj?: any[]) => {
    if (obj && obj.length > 0) return obj;
    return undefined;
  };

  private normalizeEventTriggers = (eventTriggers?: EventTrigger[]): EventTrigger[] => {
    return eventTriggers
      ? [
          ...new Set([
            ...(eventTriggers.includes(EventTrigger.TAILGATING) ? [EventTrigger.FRONT_COLLISION_WARNING] : []),
            ...(eventTriggers.includes(EventTrigger.FRONT_COLLISION_WARNING) ? [EventTrigger.TAILGATING] : []),
            ...eventTriggers,
          ]),
        ]
      : [];
  };

  public getMediaEventList = (filters: EventFilters, pageParam?: number | unknown): Promise<PageableContent<Media>> => {
    const sortList = filters.sort
      ? [filters.sort].flatMap(({ column, order }) => {
          if (column === 'severity') return [`severity.ordinal,${order}`, 'startTime,desc'];
          return `${camelCase(column)},${order}`;
        })
      : undefined;

    return graphQLRequester
      .query<{ media: PageableContentAPI<MediaAPI> }>({
        query: this.queryGetMedia,
        variables: {
          driverIds: this.getArrayKeys(filters.driverIds),
          vehicleIds: this.getArrayKeys(filters.vehicleIds),
          eventTriggers: this.getArrayKeys(this.normalizeEventTriggers(filters.eventTriggers)),
          mediaTypes: this.getArrayKeys(filters.mediaTypes),
          severities: this.getArrayKeys(filters.severityTypes),
          fromTimestamp: filters.mediaPeriod ? filters.mediaPeriod.start : undefined,
          toTimestamp: filters.mediaPeriod ? filters.mediaPeriod.end : undefined,
          page: pageParam ? pageParam : filters.page,
          size: filters.size,
          sortList,
          isFavorite: filters.favoriteMedia ? true : undefined,
          safetyOccurrenceIds: this.getArrayKeys(filters.safetyOccurrenceIds),
          mediaStatus: this.getArrayKeys(filters.mediaStatus),
          descriptionTextQuery: filters.descriptionTextQuery,
          suggestedOccurrences: this.getArrayKeys(filters.suggestedOccurrences),
        },
      })
      .then((result) => toMediaPage(result.media));
  };

  public getMediaRequestList = (filters: RequestFilters, pageParam?: number | unknown): Promise<PageableContent<Media>> =>
    graphQLRequester
      .query<{ media: PageableContentAPI<MediaAPI> }>({
        query: this.queryGetMedia,
        variables: {
          driverIds: this.getArrayKeys(filters.driverIds),
          vehicleIds: this.getArrayKeys(filters.vehicleIds),
          mediaTypes: this.getArrayKeys(filters.mediaTypes),
          page: pageParam ? pageParam : filters.page,
          size: filters.size,
          sort: filters.sort ? `${filters.sort.column.includes('created') ? 'f.' : ''}${camelCase(filters.sort.column)},${filters.sort.order}` : 'f.createdAt,desc',
          isFavorite: filters.favoriteMedia,
          requestStatus: mediaRequestStatusV2Filter(filters.requestStatus),
          createdBy: this.getArrayKeys(filters.createdBy),
          isNew: filters.watchStatus ? mediaNewFilter(filters.watchStatus) : undefined,
          fromTimestamp: filters.mediaPeriod ? filters.mediaPeriod.start : undefined,
          toTimestamp: filters.mediaPeriod ? filters.mediaPeriod.end : undefined,
          fromCreatedAtTimestamp: filters.requestedAt ? filters.requestedAt.start : undefined,
          toCreatedAtTimestamp: filters.requestedAt ? filters.requestedAt.end : undefined,
          safetyOccurrenceIds: filters.safetyOccurrenceIds ? filters.safetyOccurrenceIds : [],
          mediaStatus: this.getArrayKeys(filters.mediaStatus),
          isRequestedByTrigger: filters.isRequestedByTrigger ? filters.isRequestedByTrigger : undefined,
          triggerIds: this.getArrayKeys(filters.triggerIds),
        },
      })
      .then((result) => toMediaPage(result.media));

  public getMediaEventCount = (vehicleId: UUID, period: DateRange): Promise<number> =>
    graphQLRequester
      .query<{ media: PageableContentAPI<MediaAPI> }>({
        query: this.queryGetDeviceMediaCount,
        variables: {
          mediaTypes: [MediaType.EVENT_VIDEO],
          vehicleIds: [vehicleId],
          fromTimestamp: period.start,
          toTimestamp: period.end,
          eventTriggers: Object.values(EventTrigger).filter((item) => item !== EventTrigger.UNKNOWN),
        },
      })
      .then((result) => result.media.total_elements);

  public createMedia = async (createMediaRequest: CreateMediaRequest): Promise<CreateMediaRequestResponse> => {
    const result = await graphQLRequester.mutate<{
      createMedia: CreateMediaRequestResponseAPI;
    }>({
      mutation: this.mutateCreateMediaRequest,
      variables: {
        createMediaRequest: toCreateMediaRequestAPI(createMediaRequest),
      },
    });

    return toCreateMediaRequestResponse(result.createMedia);
  };

  public getMediaView = (mediaId: UUID, shouldGenerateDescription: Boolean): Promise<MediaView> =>
    graphQLRequester
      .query<{ mediaView: MediaViewAPI }>({
        query: this.queryGetMediaView,
        variables: {
          mediaId,
          shouldGenerateDescription,
        },
      })
      .then((result) => {
        return toMediaView(result.mediaView);
      });

  // Query enxuta só pro thumbnail (sem comentários/IA/signed url do vídeo). Usada pra preencher o
  // thumbnail das listas (1 por item) sem puxar o payload completo do mediaView.
  private queryGetMediaThumbnail = gql`
    query getMediaThumbnail($mediaId: ID) {
      mediaView(mediaId: $mediaId) {
        media_info {
          id
          media_details {
            ... on EventVideo {
              event_thumbnail_url: thumbnail_url
            }
            ... on FootageVideo {
              footage_thumbnail_url: thumbnail_url
            }
          }
        }
      }
    }
  `;

  public getMediaThumbnail = (mediaId: UUID): Promise<string | undefined> =>
    graphQLRequester
      .query<{ mediaView?: { media_info?: { media_details?: { event_thumbnail_url?: string; footage_thumbnail_url?: string } } } }>({
        query: this.queryGetMediaThumbnail,
        variables: { mediaId },
      })
      .then((result) => {
        const details = result.mediaView?.media_info?.media_details;
        return details?.event_thumbnail_url ?? details?.footage_thumbnail_url ?? undefined;
      });

  public getEventsSeveritySummary = (filters: EventFilters) =>
    graphQLRequester
      .query<{ eventsSeveritiesSummary: EventsSeveritySummary }>({
        query: this.queryEventsSeveritiesSummary,
        variables: {
          driverIds: this.getArrayKeys(filters.driverIds),
          vehicleIds: this.getArrayKeys(filters.vehicleIds),
          eventTriggers: this.getArrayKeys(this.normalizeEventTriggers(filters.eventTriggers)),
          fromTimestamp: filters.mediaPeriod ? filters.mediaPeriod.start : undefined,
          toTimestamp: filters.mediaPeriod ? filters.mediaPeriod.end : undefined,
          isFavorite: filters.favoriteMedia ? true : undefined,
          safetyOccurrenceIds: this.getArrayKeys(filters.safetyOccurrenceIds),
          mediaStatus: this.getArrayKeys(filters.mediaStatus),
          descriptionTextQuery: filters.descriptionTextQuery,
          suggestedOccurrences: this.getArrayKeys(filters.suggestedOccurrences),
        },
      })
      .then((result) => result.eventsSeveritiesSummary);
}

export default GraphQLCameraMediaRequester;
